import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  NgZone,
  OnInit,
  Output
}                           from '@angular/core';
import {
  BlockUI,
  NgBlockUI
}                           from 'ng-block-ui';
import { TranslateService } from '@ngx-translate/core';

import {
  HaynesProVesaComfortDetailDataModel,
  HaynesProVesaComponentGroupModel,
  HaynesProVesaComponentModel,
  HaynesProVesaDiagnosticConnectorModel,
  HaynesProVesaFaultCodeModel,
  HaynesProVesaNetworkModel,
  HaynesProVesaTabModel
}                                              from './typings/haynes-pro.vesa';
import { HaynesProVesaCompleteComponentModel } from './typings/haynes-pro.vesa-advanced';
import { HaynesProService }                    from '../../haynes-pro.service';
import { MessagesService }                     from '../../../messages/messages.service';
import { VehicleModel }                        from '../../../vehicles/models/vehicle';
import { VesaService }                         from './vesa.service';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-vesa-details',
  styles: [ require('./haynes-pro.diagnostic-tool.vesa-details.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa-details.component.html')
})
export class HaynesProDiagnosticToolVesaDetailsComponent implements AfterViewInit, OnInit {

  private _allDone = false;

  @BlockUI('haynesProVesaDetailsBlock') block             : NgBlockUI;
  @BlockUI('haynesProComponentDetailBlock') detailBlock   : NgBlockUI;

  @Input() data    : any;
  @Input() vehicle : VehicleModel;
  @Output() componentDetailVisible = new EventEmitter<boolean>();

  activeTab             = 0;
  filterText            = '';
  isAnimating           = false;
  selectedComponentName = '';
  showDetails           = false;

  comfortDetailData    : HaynesProVesaComfortDetailDataModel;
  componentDetailSource: 'vesa' | 'comfort_electronics' = 'vesa';
  components           : Array<HaynesProVesaComponentGroupModel>;
  connectorImage       : HaynesProVesaDiagnosticConnectorModel;
  componentDetailData  : HaynesProVesaCompleteComponentModel;
  componentHistory     : Array<{ data: HaynesProVesaCompleteComponentModel; name: string }> = [];
  faultCodes           : Array<HaynesProVesaFaultCodeModel>;
  networks             : Array<HaynesProVesaNetworkModel>;

  tabs : Array<HaynesProVesaTabModel> = [
    { key: 'components',  titleKey: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.COMPONENTS',           enabled: false, loading: true },
    { key: 'networks',    titleKey: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.CAN_BUS_OVERVIEW',     enabled: false, loading: true },
    { key: 'fault_codes', titleKey: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.FAULT_CODE_DIAGNOSIS', enabled: false, loading: true },
    { key: 'locations',   titleKey: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.ELECTRONIC_LOCATIONS', enabled: true,  loading: false }
  ];

  constructor (
    private cdr              : ChangeDetectorRef,
    @Inject(forwardRef(() => HaynesProService))
    private haynesProService : HaynesProService,
    private messagesService  : MessagesService,
    public translateService  : TranslateService,
    private vesaService      : VesaService,
    private zone             : NgZone
  ) {}

  ngAfterViewInit () : void {
    if (!this._allDone) {
      this.block.start();
    }
  }

  ngOnInit () : void {
    const vehicleId      = this.data.vehicle_id;
    const selections     = this.data.selections || [];
    const vesaSelections = selections.filter(( s : any ) => s?.source !== 'comfort_electronics');
    const systemTypeIds  = vesaSelections
      .map(( s : any ) => s.system_type_id)
      .filter(Boolean);

    // Preload the Locations tab data so it's ready when the user switches to it
    this.haynesProService.preloadLocationsBySubject(this.vehicle);

    // Components and Networks

    const comfortGroups = this.buildComfortComponentGroups(selections);

    // Components list — only hit the VESA endpoint when there's at least one VESA selection.
    const componentsPromise : Promise<void> = systemTypeIds.length > 0
      ? this.vesaService.getVesaComponentsList(vehicleId, systemTypeIds)
        .then(( data : Array<HaynesProVesaComponentGroupModel> ) => {
          const vesaGroups = ( data || [] ).map(group => ({
            ...group,
            system_info: { ...group.system_info, source: 'vesa' as const }
          }));

          this.components      = [ ...vesaGroups, ...comfortGroups ];
          this.tabs[0].enabled = this.components.length > 0;
        })
        .catch(( err : any ) => {
          this.messagesService.error(err);
          this.components      = [ ...comfortGroups ];
          this.tabs[0].enabled = this.components.length > 0;
        })
        .finally(() => {
          this.tabs[0].loading = false;
        })
      : Promise.resolve().then(() => {
          this.components      = [ ...comfortGroups ];
          this.tabs[0].enabled = this.components.length > 0;
          this.tabs[0].loading = false;
        });

    // Networks — VESA-only tab. Skips the call entirely when no VESA selections.
    const networksPromise : Promise<void> = vesaSelections.length > 0
      ? this.vesaService.getVesaNetworks(vehicleId, vesaSelections)
        .then(( data : Array<HaynesProVesaNetworkModel> ) => {
          this.networks        = data;
          this.tabs[1].enabled = data?.length > 0;
        })
        .catch(( err : any ) => this.messagesService.error(err))
        .finally(() => {
          this.tabs[1].loading = false;
        })
      : Promise.resolve().then(() => {
          this.networks        = [];
          this.tabs[1].enabled = false;
          this.tabs[1].loading = false;
        });

    // Fault codes — vehicle-wide, uses preloaded cache from diagnostic-tool(very heavy API)

    const faultCodesPromise = this.vesaService.getVesaFaultCodes(vehicleId)
    .then(( data : Array<HaynesProVesaFaultCodeModel> ) => {
      this.faultCodes      = data || [];
      this.tabs[2].enabled = this.faultCodes.length > 0;
    })
    .catch(( err : any ) => this.messagesService.error(err))
    .finally(() => {
      this.tabs[2].loading = false;
    });

    // Diagnostic connector image

    const imagePromise = this.vesaService.getDiagnosticConnectorImage(vehicleId)
    .then(( data : any ) => {
      const url = typeof data === 'string'
        ? data
        : (data?.image || data?.image_url || data?.image_path || data?.url);

      this.connectorImage = url
        ? { image_url: url, title: data?.title || data?.description }
        : null;
    })
    .catch(() => {
      this.connectorImage = null;
    });

    Promise.all([ componentsPromise, networksPromise, faultCodesPromise, imagePromise ])
    .finally(() => {
      this._allDone = true;
      this.block.stop();
      this.cdr.detectChanges();
    });
  }

  private buildComfortComponentGroups ( selections : Array<any> ) : Array<HaynesProVesaComponentGroupModel> {
    return ( selections || [] )
      .filter(s => s?.source === 'comfort_electronics' && !!s?.wiring_diagram_system_id)
      .map(s => ({
        components : [],
        system_info: {
          source            : 'comfort_electronics' as const,
          system_description: s.description || '',
          system_type_id    : s.wiring_diagram_system_id
        }
      }));
  }

  handleComponentSelect ( event : { component: HaynesProVesaComponentModel; source?: 'vesa' | 'comfort_electronics'; systemTypeId: string } ) : void {
    if (event?.source === 'comfort_electronics') {
      this.openComfortComponentDetail(event);
      return;
    }

    const vehicleId            = this.data.vehicle_id;
    this.componentDetailSource = 'vesa';
    this.comfortDetailData     = null;
    this.componentHistory      = [];
    this.selectedComponentName = '';
    this.componentDetailData   = null;
    this.isAnimating           = false;
    this.showDetails           = false;

    this.componentDetailVisible.emit(true);

    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.zone.run(() => {
            this.isAnimating = true;
            this.showDetails = true;

            this.cdr.markForCheck();

            setTimeout(() => {
              this.detailBlock.start();
              this.isAnimating = false;
            }, 450);
          });
        });
      });
    });

    // Fetch complete component data
    this.vesaService.getVesaCompleteComponent(
      vehicleId,
      event.component.id,
      event.systemTypeId
    )
    .then(( data : HaynesProVesaCompleteComponentModel ) => {
      this.componentDetailData   = data;
      this.selectedComponentName = event.component.name || ((data.component?.code || '') + ' ' + (data.component?.name || ''));
    })
    .catch(( err : any ) => {
      this.messagesService.error(err);
      this.backToTabs();
    })
    .finally(() => {
      this.detailBlock.stop();
      this.cdr.detectChanges();
    });
  }

  private openComfortComponentDetail ( event : { component: HaynesProVesaComponentModel; systemTypeId: string } ) : void {
    const group = ( this.components || [] ).find(g => g.system_info.system_type_id === event.systemTypeId);

    if (!group) {
      return;
    }

    const selection   = ( this.data?.selections || [] ).find(( s : any ) =>
      ( s?.wiring_diagram_system_id || s?.system_type_id ) === event.systemTypeId
    );
    const groupText   = ( selection as any )?.group_text   || '';
    const description = group.system_info.system_description || '';
    const contextBits = [ groupText, description ]
      .filter(v => !!v)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    const context     = contextBits.join(' - ');
    const compName    = event.component?.name || '';

    this.componentDetailSource = 'comfort_electronics';
    this.componentDetailData   = null;
    this.componentHistory      = [];
    this.selectedComponentName = context ? compName + ' | ' + context : compName;
    this.comfortDetailData     = {
      allComponents: group.components || [],
      component    : event.component,
      schemeUrl    : group.system_info.mime_data_name || ''
    };
    this.isAnimating = false;
    this.showDetails = false;

    this.componentDetailVisible.emit(true);

    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.zone.run(() => {
            this.isAnimating = true;
            this.showDetails = true;

            this.cdr.markForCheck();

            setTimeout(() => {
              this.isAnimating = false;
            }, 450);
          });
        });
      });
    });
  }

  // Save current state to history
  handleComponentNavigate ( event : { componentId: string; systemTypeId: string } ) : void {
    const vehicleId = this.data.vehicle_id;

    // Save current state to history before navigating
    if (this.componentDetailData) {
      this.componentHistory.push({
        data : this.componentDetailData,
        name : this.selectedComponentName
      });
    }

    this.componentDetailData   = null;
    this.selectedComponentName = '';
    this.detailBlock.start();

    this.vesaService.getVesaCompleteComponent(
      vehicleId,
      event.componentId,
      event.systemTypeId
    )
    .then(( data : HaynesProVesaCompleteComponentModel ) => {
      this.componentDetailData   = data;
      this.selectedComponentName = (data.component?.code || '') + ' - ' + (data.component?.name || '');
    })
    .catch(( err : any ) => {
      this.messagesService.error(err);
    })
    .finally(() => {
      this.detailBlock.stop();
      this.cdr.detectChanges();
    });
  }

  navigateBack () : void {
    if (!this.componentHistory.length) {
      return;
    }

    const previous = this.componentHistory.pop();

    this.componentDetailData   = previous.data;
    this.selectedComponentName = previous.name;
    this.cdr.detectChanges();
  }

  backToTabs () : void {
    this.componentHistory  = [];
    this.comfortDetailData = null;
    this.isAnimating       = true;
    this.showDetails       = false;
    this.componentDetailVisible.emit(false);

    this.cdr.markForCheck();

    setTimeout(() => this.isAnimating = false, 450);
  }

  onCssTransitionEnd ( evt : TransitionEvent ) : void {
    const target = evt.target as HTMLElement;

    if (!target.classList.contains('shell-details')) {
      return;
    }

    if (evt.propertyName !== 'transform' && evt.propertyName !== 'opacity') {
      return;
    }

    this.isAnimating = false;
  }

  resetFilter () : void {
    this.filterText = '';
  }
}
